<template>
  <label v-if="iconOnly" class="iconBtn" :class="{ disabled: busy }" :title="busy ? 'Uploading…' : 'Add contacts from CSV'">
    <input type="file" accept=".csv" class="csvFileInput" @change="onFileSelected" :disabled="busy" />
    <AppIcon name="import_csv" :size="16" />
  </label>
  <label v-else class="btn secondary" :class="{ disabled: busy }">
    <input type="file" accept=".csv" class="csvFileInput" @change="onFileSelected" :disabled="busy" />
    <span>{{ busy ? 'Uploading…' : 'Add contacts from CSV' }}</span>
  </label>
  <p v-if="notice" class="csvNotice">{{ notice }}</p>

  <Teleport to="body">
    <Modal v-if="preview" title="Confirm CSV Import" @close="cancel">
      <div class="csvPreviewBody">
        <p class="csvPreviewIntro">The following changes will be applied:</p>
        <div class="csvPreviewSection">
          <div><strong>{{ preview.valid_rows }}</strong> valid contact{{ preview.valid_rows !== 1 ? 's' : '' }} found out of {{ preview.total_rows }} row{{ preview.total_rows !== 1 ? 's' : '' }}</div>
        </div>
        <div v-if="preview.skipped_no_email > 0" class="csvPreviewWarn">
          {{ preview.skipped_no_email }} row{{ preview.skipped_no_email !== 1 ? 's' : '' }} skipped — no valid email address.
        </div>
        <div v-if="preview.skipped_no_domain > 0" class="csvPreviewWarn">
          {{ preview.skipped_no_domain }} row{{ preview.skipped_no_domain !== 1 ? 's' : '' }} skipped — no domain could be resolved.
        </div>
      </div>
      <template #footer>
        <button class="btn secondary" @click="cancel">Cancel</button>
        <button class="btn primary" :disabled="busy" @click="commit">
          {{ busy ? 'Importing…' : 'Import' }}
        </button>
      </template>
    </Modal>
  </Teleport>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import Modal from './Modal.vue'
import AppIcon from '../../components/AppIcon.vue'
import { apiFetch } from '../../services/api'

const props = defineProps<{
  playId: string
  refreshPlay: () => Promise<void>
  iconOnly?: boolean
}>()

const busy = ref(false)
const notice = ref('')
const preview = ref<any>(null)
const pendingFile = ref<File | null>(null)

async function onFileSelected(evt: Event) {
  const input = evt.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file) return
  busy.value = true
  preview.value = null
  try {
    const fd = new FormData()
    fd.append('file', file)
    const resp: any = await apiFetch(
      `/sales-plays/${encodeURIComponent(props.playId)}/contacts/import_csv/preview`,
      { method: 'POST', body: fd },
    )
    pendingFile.value = file
    preview.value = resp
  } catch (e: any) {
    notice.value = e?.message || 'CSV preview failed'
  } finally {
    busy.value = false
  }
}

function cancel() {
  preview.value = null
  pendingFile.value = null
}

async function commit() {
  if (!pendingFile.value) return
  busy.value = true
  try {
    const fd = new FormData()
    fd.append('file', pendingFile.value)
    const resp: any = await apiFetch(
      `/sales-plays/${encodeURIComponent(props.playId)}/contacts/import_csv`,
      { method: 'POST', body: fd },
    )
    const staged = Number(resp?.newly_staged_count ?? 0)
    notice.value = `Imported ${staged} candidate(s) from CSV.`
    cancel()
    await props.refreshPlay()
  } catch (e: any) {
    notice.value = e?.message || 'CSV import failed'
  } finally {
    busy.value = false
  }
}
</script>

<style scoped>
.csvFileInput { display: none; }
.disabled { opacity: 0.5; cursor: not-allowed; }
.iconBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  background: none;
  color: var(--color-text-secondary);
  cursor: pointer;
}
.iconBtn:hover { color: var(--color-text-primary); background: var(--color-bg-subtle); }
.csvNotice {
  margin: 0;
  padding: var(--space-2) var(--space-3);
  background: var(--color-accent-soft);
  color: var(--color-accent);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--fs-sm);
}
.csvPreviewBody { display: flex; flex-direction: column; gap: var(--space-4); font-family: var(--font-sans); font-size: var(--fs-sm); color: var(--color-text-primary); line-height: var(--lh-normal); }
.csvPreviewIntro { margin: 0; color: var(--color-text-secondary); }
.csvPreviewSection { display: flex; flex-direction: column; gap: var(--space-1); }
.csvPreviewWarn { color: var(--color-warning, #c2410c); }
</style>
